import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createTreasuryFund,
  listCommunityTreasuryFunds,
  registerTreasuryFundTransaction,
  setTreasuryFundBalance,
  setTreasuryFundDonationConfig,
  setTreasuryFundReconciliationReview,
} from '../../services/TreasuryService';
import { normalizeFundsPayload } from './treasuryModels';
import { readTreasuryPostingRequest, readFundReference, readFundCreateRequest } from '@liquid-spirit/api/domains/treasury';

export function useTreasuryFunds({
  token,
  communityId,
  clearMessages,
  setError,
  setSuccess,
  setSubmitting,
}) {
  const alive = useRef(false), version = useRef(0), busy = useRef(false), readAbort = useRef(null);
  const options = useMemo(() => ({token, ...(communityId ? {params: {communityId}} : {})}), [token, communityId]);
  useEffect(() => {alive.current = true; return () => {alive.current = false; version.current += 1; readAbort.current?.abort();};}, []);
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(Boolean(token));
  const [isFallbackData, setIsFallbackData] = useState(false);
  const [hasBackendFundsData, setHasBackendFundsData] = useState(false);
  const [hasLoadedFunds, setHasLoadedFunds] = useState(false);

  const loadFunds = useCallback(async ({ silent = false } = {}) => {
    if (!alive.current) return {success: false};
    const request = ++version.current;
    readAbort.current?.abort();
    const controller = new AbortController();
    readAbort.current = controller;
    if (!token) {
      if (!silent) {
        setLoading(false);
      }
      setFunds([]);
      setIsFallbackData(false);
      setHasBackendFundsData(false);
      setHasLoadedFunds(false);
      return { success: false, error: 'Missing authentication token' };
    }

    if (!silent) {
      setLoading(true);
      setError('');
    }

    try {
      const payload = await listCommunityTreasuryFunds({...options, signal: controller.signal});
      if (!alive.current || request !== version.current) return {success: false};
      const rows = normalizeFundsPayload(payload?.data || payload?.funds || payload);

      if (rows.length > 0) {
        setFunds(rows.map((row) => ({
          ...row,
          status: 'live',
        })));
        setIsFallbackData(false);
        setHasBackendFundsData(true);
        setError('');
      } else {
        setFunds([]);
        setIsFallbackData(false);
        setHasBackendFundsData(true);
        setError('');
      }
      setHasLoadedFunds(true);
    } catch (err) {
      if (!alive.current || request !== version.current) return {success: false};
      console.error('Failed to load treasury funds:', err);
      setFunds([]);
      setIsFallbackData(false);
      setHasBackendFundsData(false);
      setHasLoadedFunds(true);
      setError('Could not load treasury balances. Please try again.');
      return {
        success: false,
        error: err?.message || 'Unable to load treasury funds',
      };
    } finally {
      if (alive.current && request === version.current && !silent) {
        setLoading(false);
      }
    }

    return { success: true };
  }, [options, setError, token]);

  const createFund = useCallback(async ({ code, fundName, name, currency, description, balance }) => {
    clearMessages();

    try {readFundCreateRequest({code, fundName, name, currency, description, balance});}
    catch (err) {setError(err.message); return {success: false, message: err.message};}
    if (!alive.current || busy.current) return {success: false};
    busy.current = true; version.current += 1;
    setSubmitting(true);

    try {
      await createTreasuryFund({...options, code, fundName, name, currency, description, balance});
      if (!alive.current) return {success: false};

      await loadFunds({ silent: true });
      if (!alive.current) return {success: false};
      setSuccess('Fund created successfully.');
      return { success: true };
    } catch (err) {
      if (!alive.current) return {success: false};
      const message = err?.code === 'INVALID_API_RESPONSE' || err?.code !== 'INVALID_API_REQUEST' && (!err?.status || err.status >= 500)
        ? 'Fund creation could not be confirmed. Refresh funds before trying again.' : err.message || 'Failed to create fund.';
      console.error('Failed to create treasury fund:', err);
      setError(message);
      return { success: false, message };
    } finally {
      busy.current = false;
      if (alive.current) {setSubmitting(false); setLoading(false);}
    }
  }, [clearMessages, loadFunds, setError, setSubmitting, setSuccess, options]);

  const setFundBalance = useCallback(async ({
    fundId,
    balance,
    note,
  }) => {
    if (!alive.current || busy.current) return {success: false};
    clearMessages();

    try {readFundReference(fundId); readTreasuryPostingRequest({balance, note}, 'set_balance');}
    catch (err) {setError(err.message); return {success: false, message: err.message};}
    busy.current = true; version.current += 1;

    setSubmitting(true);

    try {
      await setTreasuryFundBalance({
        ...options,
        fundId,
        balance,
        note,
      });

      if (!alive.current) return {success: false};
      await loadFunds({ silent: true });
      if (!alive.current) return {success: false};
      setSuccess('Fund balance updated successfully.');
      return { success: true };
    } catch (err) {
      if (!alive.current) return {success: false};
      const message = err?.message || 'Failed to update fund balance.';
      console.error('Failed to set treasury fund balance:', err);
      setError(message);
      return { success: false, message };
    } finally {
      busy.current = false;
      if (alive.current) {setSubmitting(false); setLoading(false);}
    }
  }, [clearMessages, loadFunds, setError, setSubmitting, setSuccess, options]);

  const addBalance = useCallback(async ({ fundId, amount, note }) => {
    if (!alive.current || busy.current) return {success: false};
    clearMessages();

    try {readFundReference(fundId); readTreasuryPostingRequest({amount, note}, 'transaction');}
    catch (err) {setError(err.message); return {success: false, message: err.message};}
    busy.current = true; version.current += 1;

    setSubmitting(true);

    try {
      await registerTreasuryFundTransaction({
        ...options,
        fundId,
        amount,
        note,
      });

      if (!alive.current) return {success: false};
      await loadFunds({ silent: true });
      if (!alive.current) return {success: false};
      setSuccess(Number(amount) > 0 ? 'Transaction added successfully.' : 'Transaction deducted successfully.');
      return { success: true };
    } catch (err) {
      if (!alive.current) return {success: false};
      console.error('Failed to register treasury transaction:', err);
      const message = err?.message || 'Failed to register transaction.';
      setError(message);
      return { success: false, message };
    } finally {
      busy.current = false;
      if (alive.current) {setSubmitting(false); setLoading(false);}
    }
  }, [clearMessages, loadFunds, setError, setSubmitting, setSuccess, options]);

  const setFundReconciliationReview = useCallback(async ({ fundId, status, note = '' }) => {
    if (!alive.current || busy.current) return {success: false};
    busy.current = true; version.current += 1;
    clearMessages();
    setSubmitting(true);

    try {
      await setTreasuryFundReconciliationReview({ ...options, fundId, status, note });
      if (!alive.current) return {success: false};
      await loadFunds({ silent: true });
      if (!alive.current) return {success: false};
      setSuccess(status === 'reconciled' ? 'Fund marked reconciled.' : status === 'reviewed' ? 'Fund marked reviewed.' : 'Fund review state cleared.');
      return { success: true };
    } catch (err) {
      if (!alive.current) return {success: false};
      const message = err?.message || 'Failed to update reconciliation review state.';
      setError(message);
      return { success: false, message };
    } finally {
      busy.current = false;
      if (alive.current) {setSubmitting(false); setLoading(false);}
    }
  }, [clearMessages, loadFunds, setError, setSubmitting, setSuccess, options]);

  const setFundDonationConfig = useCallback(async ({ fundId, donationConfig }) => {
    if (!alive.current || busy.current) return {success: false};
    busy.current = true; version.current += 1;
    clearMessages();
    setSubmitting(true);

    try {
      await setTreasuryFundDonationConfig({ ...options, fundId, donationConfig });
      if (!alive.current) return {success: false};
      await loadFunds({ silent: true });
      if (!alive.current) return {success: false};
      setSuccess('Donation settings updated successfully.');
      return { success: true };
    } catch (err) {
      if (!alive.current) return {success: false};
      const message = err?.message || 'Failed to update donation settings.';
      setError(message);
      return { success: false, message };
    } finally {
      busy.current = false;
      if (alive.current) {setSubmitting(false); setLoading(false);}
    }
  }, [clearMessages, loadFunds, setError, setSubmitting, setSuccess, options]);

  useEffect(() => {
    if (!token) {
      setFunds([]);
      setLoading(false);
      setIsFallbackData(false);
      setHasBackendFundsData(false);
      setHasLoadedFunds(false);
      setError('');
      return;
    }

    void loadFunds();
  }, [loadFunds, setError, token]);

  return {
    funds,
    setFunds,
    loading,
    isFallbackData,
    hasBackendFundsData,
    hasLoadedFunds,
    loadFunds,
    createFund,
    setFundBalance,
    addBalance,
    setFundReconciliationReview,
    setFundDonationConfig,
  };
}
